import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { agentService } from 'api/services/agentService';
import type { Agent, CreateAgentRequest, UpdateAgentRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import PartyBillingSection, { type PartyBillingValues } from 'components/common/billing/PartyBillingSection';
import StateSelect from 'components/common/billing/StateSelect';
import { findState, usesStatePicker } from 'components/common/billing/taxAddress';
import SectionedModalShell, { ShellBlockers, type ShellSection } from 'components/modals/common/SectionedModalShell';
import { useTaxPack } from 'hooks/api/useTaxPack';
import type { SettlementMode } from 'api/types/settlement.types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface AgentModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Agent | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordId?: string) => void;
}

interface AgentFormData {
  agentCode: string;
  agentName: string;
  agentType: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  commissionType: string;
  commissionPercent: string;
  creditDays: string;
  creditLimit: string;
  gstNumber: string;
  panNumber: string;
  allowBlocking: boolean;
  maxBlocks: string;
  blockHoldHours: string;
  isCreditEligible: boolean;
  isActive: boolean;
  gstLegalName: string;
  defaultSettlementMode: SettlementMode;
  showRatesOnGuestStatement: boolean;
  earlyDepartureRetention: string;
  isGstRegistered: boolean;
  defaultSupplyType: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: AgentFormData = {
  agentCode: '',
  agentName: '',
  agentType: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
  commissionType: '',
  commissionPercent: '',
  creditDays: '',
  creditLimit: '',
  gstNumber: '',
  panNumber: '',
  allowBlocking: false,
  maxBlocks: '',
  blockHoldHours: '',
  isCreditEligible: false,
  isActive: true,
  gstLegalName: '',
  defaultSettlementMode: 'GUEST',
  showRatesOnGuestStatement: false,
  earlyDepartureRetention: '',
  isGstRegistered: false,
  defaultSupplyType: 'STANDARD',
};

// Sections of the form (slice 3a-ii) and the fields each holds — a validation
// error badges its section and the first one is opened.
type SectionId = 'general' | 'address' | 'commercial' | 'billing';
const SECTION_OF: Record<string, SectionId> = {
  agentCode: 'general', agentName: 'general', agentType: 'general', isActive: 'general',
  contactPerson: 'address', phone: 'address', email: 'address', address: 'address',
  city: 'address', state: 'address', country: 'address', pincode: 'address',
  commissionType: 'commercial', commissionPercent: 'commercial', creditDays: 'commercial', creditLimit: 'commercial',
  isGstRegistered: 'billing', gstNumber: 'billing', gstLegalName: 'billing', panNumber: 'billing',
  defaultSupplyType: 'billing', defaultSettlementMode: 'billing',
};
const SECTION_TITLES: Record<SectionId, string> = {
  general: 'General', address: 'Address & Contact', commercial: 'Commission & Credit', billing: 'Billing & Tax',
};

/** The form values of a stored agent. */
const toFormData = (rec: Agent, mode: 'create' | 'edit' | 'copy'): AgentFormData => ({
  agentCode: mode === 'copy' ? '' : (rec.agentCode || ''),
  agentName: rec.agentName || '',
  agentType: rec.agentType || '',
  contactPerson: rec.contactPerson || '',
  phone: rec.phone || '',
  email: rec.email || '',
  address: rec.address || '',
  city: rec.city || '',
  state: rec.state || '',
  country: rec.country || '',
  pincode: rec.pincode || '',
  commissionType: rec.commissionType || '',
  commissionPercent: rec.commissionPercent != null ? String(rec.commissionPercent) : '',
  creditDays: rec.creditDays != null ? String(rec.creditDays) : '',
  creditLimit: rec.creditLimit != null ? String(rec.creditLimit) : '',
  gstNumber: rec.gstNumber || '',
  panNumber: rec.panNumber || '',
  allowBlocking: rec.allowBlocking ?? false,
  maxBlocks: rec.maxBlocks != null ? String(rec.maxBlocks) : '',
  blockHoldHours: rec.blockHoldHours != null ? String(rec.blockHoldHours) : '',
  isCreditEligible: rec.isCreditEligible ?? false,
  gstLegalName: rec.gstLegalName || '',
  defaultSettlementMode: rec.defaultSettlementMode || 'GUEST',
  showRatesOnGuestStatement: rec.showRatesOnGuestStatement ?? false,
  earlyDepartureRetention: rec.earlyDepartureRetention ?? '',
  isGstRegistered: rec.isGstRegistered ?? !!rec.gstNumber,
  defaultSupplyType: rec.defaultSupplyType || 'STANDARD',
  isActive: rec.isActive,
});

const AGENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Travel Agent', label: 'Travel Agent' },
  { value: 'Tour Operator', label: 'Tour Operator' },
  { value: 'Wholesale', label: 'Wholesale' },
];

const COMMISSION_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Flat Amount', label: 'Flat Amount' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const AgentModal: React.FC<AgentModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<AgentFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- Sections (slice 3a-ii): left nav, error badges per section ---
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const { pack } = useTaxPack();

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<Agent | null | undefined>(record);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: AgentFormData = (mode === 'edit' || mode === 'copy') && rec ? toFormData(rec, mode) : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setActiveSection('general');
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  // The Billing & Tax section edits its fields in place.
  const handleBillingChange = useCallback((patch: Partial<PartyBillingValues>) => {
    if ('gstNumber' in patch) clearFieldError('gstNumber');
    setFormData(prev => ({ ...prev, ...patch }));
  }, [clearFieldError]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.agentCode?.trim();
      const name = currentFormData.agentName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const code = currentFormData.agentCode?.trim();
      const name = currentFormData.agentName?.trim();
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.agentCode || currentFormData.agentName || 'Agent'}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? recordRef.current?.id : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.agentCode.trim()) {
      errors.agentCode = 'Agent Code is required';
    } else if (formData.agentCode.length > 20) {
      errors.agentCode = 'Agent Code cannot exceed 20 characters';
    }

    if (!formData.agentName.trim()) {
      errors.agentName = 'Agent Name is required';
    } else if (formData.agentName.length > 100) {
      errors.agentName = 'Agent Name cannot exceed 100 characters';
    }

    if (!formData.agentType) {
      errors.agentType = 'Agent Type is required';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required';
    } else if (!formData.isGstRegistered && usesStatePicker(pack, formData.country) && !findState(pack, formData.state)) {
      errors.state = 'Pick the state from the list — an unregistered agent is taxed by it';
    }

    if (formData.isGstRegistered && !formData.gstNumber.trim()) {
      errors.gstNumber = 'GSTIN is required for a registered agent';
    }

    // Numeric validations
    if (formData.commissionPercent) {
      const val = parseFloat(formData.commissionPercent);
      if (isNaN(val) || val < 0 || val > 100) {
        errors.commissionPercent = 'Commission must be between 0 and 100';
      }
    }

    if (formData.creditDays) {
      const val = parseInt(formData.creditDays, 10);
      if (isNaN(val) || val < 0) {
        errors.creditDays = 'Credit Days must be a positive integer';
      }
    }

    if (formData.creditLimit) {
      const val = parseFloat(formData.creditLimit);
      if (isNaN(val) || val < 0) {
        errors.creditLimit = 'Credit Limit must be a positive number';
      }
    }

    // Note: maxBlocks / blockHoldHours validators dropped — the Blocking
    // section has been removed from the UI; values pass through silently
    // on edit. If those fields are ever resurfaced, restore the validators.

    // Code uniqueness validation (async)
    if (!errors.agentCode && formData.agentCode.trim()) {
      try {
        const excludeId = mode === 'edit' ? record?.id : undefined;
        const res = await agentService.validateCode(formData.agentCode.trim(), excludeId);
        if (res.data === false) {
          errors.agentCode = 'This Agent Code already exists';
        }
      } catch {
        // Skip validation on network error — server will catch duplicate
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const first = Object.keys(errors)[0];
      setActiveSection(SECTION_OF[first] ?? 'general');
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record, pack]);

  // --- Confirmed save (3-stage: confirm → loading → toast) ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateAgentRequest | UpdateAgentRequest = {
        agentCode: formData.agentCode.trim(),
        agentName: formData.agentName.trim(),
        agentType: formData.agentType || undefined,
        contactPerson: formData.contactPerson.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state.trim() || undefined,
        country: formData.country.trim() || undefined,
        pincode: formData.pincode.trim() || undefined,
        commissionType: formData.commissionType || undefined,
        commissionPercent: formData.commissionPercent ? parseFloat(formData.commissionPercent) : undefined,
        creditDays: formData.creditDays ? parseInt(formData.creditDays, 10) : undefined,
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : undefined,
        // An unregistered agent has no GSTIN (the server clears it too).
        gstNumber: formData.isGstRegistered ? formData.gstNumber.trim() || undefined : undefined,
        isGstRegistered: formData.isGstRegistered,
        defaultSupplyType: formData.defaultSupplyType,
        panNumber: formData.panNumber.trim() || undefined,
        allowBlocking: formData.allowBlocking,
        maxBlocks: formData.maxBlocks ? parseInt(formData.maxBlocks, 10) : undefined,
        blockHoldHours: formData.blockHoldHours ? parseFloat(formData.blockHoldHours) : undefined,
        isCreditEligible: formData.isCreditEligible,
        gstLegalName: formData.gstLegalName.trim() || null,
        defaultSettlementMode: formData.defaultSettlementMode,
        showRatesOnGuestStatement: formData.showRatesOnGuestStatement,
        earlyDepartureRetention: formData.earlyDepartureRetention || null,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await agentService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateAgentRequest);
      } else {
        await agentService.create(payload as CreateAgentRequest);
      }

      setIsSaving(false);
      notificationService.toast.success(
        `"${formData.agentCode}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await agentService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data = toFormData(rec, 'edit');
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  const err = getFieldError;
  const set = <K extends keyof AgentFormData>(field: K, value: AgentFormData[K]) => {
    clearFieldError(field as string);
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  const errorsIn = (id: SectionId) => Object.keys(fieldErrors).filter(f => (SECTION_OF[f] ?? 'general') === id).length;

  const sections: ShellSection[] = [
    {
      id: 'general',
      title: SECTION_TITLES.general,
      errorCount: errorsIn('general'),
      content: (
        <div className="form-grid-2">
          <div className="form-group" data-field="agentCode">
            <label className="form-label">Agent Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${err('agentCode') ? ' form-field-error' : ''}`}
              value={formData.agentCode}
              onChange={(e) => set('agentCode', e.target.value.toUpperCase())}
              placeholder="e.g., AGT001"
              maxLength={20}
              autoFocus={mode === 'create' || mode === 'copy'}
              disabled={mode === 'edit'}
            />
            {err('agentCode') && <div className="form-error-message">{err('agentCode')}</div>}
          </div>
          <div className="form-group" data-field="agentName">
            <label className="form-label">Agent Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${err('agentName') ? ' form-field-error' : ''}`}
              value={formData.agentName}
              onChange={(e) => set('agentName', e.target.value)}
              placeholder="e.g., ABC Travels"
              maxLength={100}
            />
            {err('agentName') && <div className="form-error-message">{err('agentName')}</div>}
          </div>
          <div className="form-group" data-field="agentType">
            <SyutSelect
              label="Agent Type"
              required
              options={AGENT_TYPE_OPTIONS}
              value={formData.agentType}
              onChange={(val) => set('agentType', (val as string) || '')}
              placeholder="Select agent type..."
              isClearable
              size="md"
              error={err('agentType')}
            />
          </div>
          <div className="form-group" data-field="isActive">
            <SyutSelect
              label="Status"
              options={[
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
              ]}
              value={String(formData.isActive)}
              onChange={(val) => set('isActive', val === 'true')}
              isClearable={false}
              size="md"
            />
          </div>
        </div>
      ),
    },
    {
      id: 'address',
      title: SECTION_TITLES.address,
      errorCount: errorsIn('address'),
      content: (
        <>
          <div className="form-grid-3">
            <div className="form-group" data-field="contactPerson">
              <label className="form-label">Contact Person</label>
              <input type="text" className="form-input" value={formData.contactPerson}
                onChange={(e) => set('contactPerson', e.target.value)} placeholder="e.g., John Doe" />
            </div>
            <div className="form-group" data-field="phone">
              <label className="form-label">Phone <span className="text-danger">*</span></label>
              <input type="text" className={`form-input${err('phone') ? ' form-field-error' : ''}`} value={formData.phone}
                onChange={(e) => set('phone', e.target.value)} placeholder="e.g., +91 9876543210" />
              {err('phone') && <div className="form-error-message">{err('phone')}</div>}
            </div>
            <div className="form-group" data-field="email">
              <label className="form-label">Email <span className="text-danger">*</span></label>
              <input type="email" className={`form-input${err('email') ? ' form-field-error' : ''}`} value={formData.email}
                onChange={(e) => set('email', e.target.value)} placeholder="e.g., agent@example.com" />
              {err('email') && <div className="form-error-message">{err('email')}</div>}
            </div>
          </div>
          <div className="form-group" data-field="address" style={{ marginTop: '0.5rem' }}>
            <label className="form-label">Address</label>
            <input type="text" className="form-input" value={formData.address}
              onChange={(e) => set('address', e.target.value)} placeholder="e.g., 123 Main Street" />
          </div>
          <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
            <div className="form-group" data-field="city">
              <label className="form-label">City <span className="text-danger">*</span></label>
              <input type="text" className={`form-input${err('city') ? ' form-field-error' : ''}`} value={formData.city}
                onChange={(e) => set('city', e.target.value)} placeholder="e.g., Mumbai" />
              {err('city') && <div className="form-error-message">{err('city')}</div>}
            </div>
            <StateSelect
              value={formData.state}
              onChange={(v) => set('state', v)}
              pack={pack}
              country={formData.country}
              required
              error={err('state')}
            />
            <div className="form-group" data-field="country">
              <label className="form-label">Country</label>
              <input type="text" className="form-input" value={formData.country}
                onChange={(e) => set('country', e.target.value)} placeholder="India (empty = India)" />
            </div>
            <div className="form-group" data-field="pincode">
              <label className="form-label">PIN Code</label>
              <input type="text" className="form-input" value={formData.pincode}
                onChange={(e) => set('pincode', e.target.value)} placeholder="e.g., 400001" maxLength={10} />
            </div>
          </div>
        </>
      ),
    },
    {
      id: 'commercial',
      title: SECTION_TITLES.commercial,
      errorCount: errorsIn('commercial'),
      content: (
        <>
          <div className="form-grid-2">
            <div className="form-group" data-field="commissionType">
              <SyutSelect
                label="Commission Type"
                options={COMMISSION_TYPE_OPTIONS}
                value={formData.commissionType}
                onChange={(val) => set('commissionType', (val as string) || '')}
                placeholder="Select..."
                isClearable
                size="md"
              />
            </div>
            <div className="form-group" data-field="commissionPercent">
              <label className="form-label">Commission %</label>
              <input type="number" className={`form-input${err('commissionPercent') ? ' form-field-error' : ''}`}
                value={formData.commissionPercent} onChange={(e) => set('commissionPercent', e.target.value)}
                placeholder="0.00" min="0" max="100" step="0.01" />
              {err('commissionPercent') && <div className="form-error-message">{err('commissionPercent')}</div>}
            </div>
            <div className="form-group" data-field="creditDays">
              <label className="form-label">Credit Days</label>
              <input type="number" className={`form-input${err('creditDays') ? ' form-field-error' : ''}`}
                value={formData.creditDays} onChange={(e) => set('creditDays', e.target.value)} placeholder="0" min="0" step="1" />
              {err('creditDays') && <div className="form-error-message">{err('creditDays')}</div>}
            </div>
            <div className="form-group" data-field="creditLimit">
              <label className="form-label">Credit Limit</label>
              <input type="number" className={`form-input${err('creditLimit') ? ' form-field-error' : ''}`}
                value={formData.creditLimit} onChange={(e) => set('creditLimit', e.target.value)} placeholder="0.00" min="0" step="0.01" />
              {err('creditLimit') && <div className="form-error-message">{err('creditLimit')}</div>}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.75rem' }}>
            <input type="checkbox" checked={formData.isCreditEligible}
              onChange={(e) => set('isCreditEligible', e.target.checked)}
              style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              Credit Eligible <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>(bookings save as CONFIRMED without advance; allows credit settlement)</span>
            </span>
          </label>
        </>
      ),
    },
    {
      id: 'billing',
      title: SECTION_TITLES.billing,
      errorCount: errorsIn('billing'),
      content: (
        <PartyBillingSection
          kind="AGENT"
          mode={mode}
          values={{
            isGstRegistered: formData.isGstRegistered,
            gstNumber: formData.gstNumber,
            gstLegalName: formData.gstLegalName,
            defaultSupplyType: formData.defaultSupplyType,
            defaultSettlementMode: formData.defaultSettlementMode,
            showRatesOnGuestStatement: formData.showRatesOnGuestStatement,
            earlyDepartureRetention: formData.earlyDepartureRetention,
          }}
          isCreditEligible={formData.isCreditEligible}
          onChange={handleBillingChange}
          pack={pack}
          addressState={formData.state}
          addressCountry={formData.country}
          addressSectionTitle={SECTION_TITLES.address}
          gstError={err('gstNumber')}
          disabled={isSaving}
          extra={
            <div className="form-group" data-field="panNumber">
              <label className="form-label">PAN</label>
              <input type="text" className="form-input" value={formData.panNumber} maxLength={10}
                onChange={(e) => set('panNumber', e.target.value.toUpperCase())} placeholder="e.g., AAPFU0939F" />
            </div>
          }
        />
      ),
    },
  ];

  const blockers = (Object.keys(SECTION_TITLES) as SectionId[])
    .filter(id => errorsIn(id) > 0)
    .map(id => ({ label: `${SECTION_TITLES[id]}: ${errorsIn(id)} to fix`, sectionId: id }));

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <SectionedModalShell
        title={mode === 'create' ? 'Add Agent' : mode === 'copy' ? 'Copy Agent' : 'Edit Agent'}
        summary={formData.agentName || formData.agentCode ? (
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{formData.agentName || '—'}</strong>
            {formData.agentCode && <> · {formData.agentCode}</>}
            {formData.isGstRegistered && formData.gstNumber && <> · GSTIN {formData.gstNumber}</>}
          </span>
        ) : undefined}
        sections={sections}
        activeSection={activeSection}
        onSectionChange={(id) => setActiveSection(id as SectionId)}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={isSaving}
        loading={{ active: isSaving, label: mode === 'edit' ? 'Saving...' : 'Creating...' }}
        maxWidth="960px"
        footer={
          <>
            <ShellBlockers blockers={blockers} onJump={(id) => setActiveSection(id as SectionId)} />
            <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving || (mode === 'edit' && !isDirty)}
              title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
            >
              <IconSystem.save size={16} />
              {mode === 'edit' ? 'Save Changes' : 'Create Agent'}
            </button>
          </>
        }
      />
      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the agent "${formData.agentCode}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default AgentModal;
